/**
 * EnrollmentManager — Admin provisioning panel for Face ID / biometric enrollment.
 *
 * Workflow:
 *   1. Admin selects a new hire from the users list
 *   2. Optionally enters a device label (e.g. "Shop Floor iPad #3")
 *   3. Clicks "Generate Enrollment QR" — valid 48 hours, single-use
 *   4. QR code displayed for the new hire to scan on the floor device
 *   5. Active tokens table shows all outstanding tokens with one-click Revoke
 */
import { useState, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import {
  Box, Card, Container, Typography, Divider, Stack, Button,
  TextField, Autocomplete, Alert, Breadcrumbs, CircularProgress,
  Chip, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, IconButton, Tooltip,
} from "@mui/material";
import {
  HomeRounded, QrCode2Outlined, FingerprintOutlined,
  DeleteOutline, RefreshOutlined, WarningAmberOutlined,
} from "@mui/icons-material";
import { QRCodeSVG } from "qrcode.react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getUsers } from "../../feature/userSlice";
import ApiCall from "../../utils/apicall";
import { showErrorToast, showSuccessToast } from "../../utils/Toast";

import SmartTable from "../../components/SmartTable/SmartTable";
// ─────────────────────────────────────────────────────────────────────────────
function formatExpiry(expiresAt, expired) {
  const d = new Date(expiresAt);
  const hoursLeft = Math.round((d - Date.now()) / 3_600_000);
  if (expired) return { label: "Expired", color: "error" };
  if (hoursLeft <= 4) return { label: `${hoursLeft}h left`, color: "warning" };
  return { label: `${hoursLeft}h left`, color: "success" };
}

// ─────────────────────────────────────────────────────────────────────────────
export default function EnrollmentManager() {
  const dispatch  = useDispatch();
  const usersList = useSelector((s) => s.userSlice.usersList);

  const [selectedUser, setSelectedUser] = useState(null);
  const [deviceLabel,  setDeviceLabel]  = useState("");
  const [generating,   setGenerating]   = useState(false);
  const [result,       setResult]       = useState(null);

  const [activeTokens,  setActiveTokens]  = useState([]);
  const [tokensLoading, setTokensLoading] = useState(true);
  const [revoking,      setRevoking]      = useState(null); // id being revoked

  useEffect(() => {
    if (!usersList.length) dispatch(getUsers());
  }, [dispatch, usersList.length]);

  const fetchActiveTokens = useCallback(async () => {
    setTokensLoading(true);
    try {
      const res = await ApiCall.get("/webauthn/enrollment-tokens");
      setActiveTokens(res.data?.data ?? []);
    } catch {
      setActiveTokens([]);
    } finally {
      setTokensLoading(false);
    }
  }, []);

  useEffect(() => { fetchActiveTokens(); }, [fetchActiveTokens]);

  const handleGenerate = async () => {
    if (!selectedUser) return;
    setGenerating(true);
    setResult(null);
    try {
      const res = await ApiCall.post("/webauthn/enrollment-token", {
        userId:      selectedUser.id,
        deviceLabel: deviceLabel.trim() || undefined,
      });
      setResult(res.data?.data);
      fetchActiveTokens(); // refresh the active tokens list
    } catch (err) {
      showErrorToast(err?.response?.data?.message || "Failed to generate enrollment token");
    } finally {
      setGenerating(false);
    }
  };

  const handleRevoke = async (id, name) => {
    setRevoking(id);
    try {
      await ApiCall.delete(`/webauthn/enrollment-tokens/${id}`);
      showSuccessToast(`Revoked token for ${name}`);
      fetchActiveTokens();
      // If the revoked token matches the currently displayed QR, clear it
      if (result && activeTokens.find((t) => t.id === id)) setResult(null);
    } catch (err) {
      showErrorToast(err?.response?.data?.message || "Failed to revoke token");
    } finally {
      setRevoking(null);
    }
  };

  const expiresInHours = result
    ? Math.round((new Date(result.expiresAt) - Date.now()) / 3_600_000)
    : null;

  return (
    <>
      <Helmet>
        <title>Enrollment Manager | SFM</title>
      </Helmet>

      <Container maxWidth="lg">
        {/* Breadcrumb */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link to="/dashboard/app" style={{ display: "flex", alignItems: "center", color: "inherit", textDecoration: "none" }}>
            <HomeRounded fontSize="small" sx={{ mr: 0.5 }} /> Home
          </Link>
          <Typography color="text.primary">Enrollment Manager</Typography>
        </Breadcrumbs>

        <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5 }}>
          Biometric Enrollment Manager
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 4 }}>
          Generate a QR code for a new hire. They scan it on the floor device to set up Face ID — no password needed.
          Tokens expire after 48 hours. Re-generate any time in seconds.
        </Typography>

        {/* ── Top row: generator + QR */}
        <Stack direction={{ xs: "column", md: "row" }} spacing={3} sx={{ mb: 3 }}>

          {/* Left: generator form */}
          <Card sx={{ p: 3, flex: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2.5 }}>
              <FingerprintOutlined sx={{ color: "primary.main", fontSize: 26 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Generate Enrollment QR
              </Typography>
            </Stack>
            <Divider sx={{ mb: 2.5 }} />

            <Autocomplete
              options={usersList}
              getOptionLabel={(u) => `${u.firstName} ${u.lastName} (${u.email})`}
              value={selectedUser}
              onChange={(_, v) => { setSelectedUser(v); setResult(null); }}
              renderInput={(params) => (
                <TextField {...params} label="Select new hire *" fullWidth sx={{ mb: 2 }} />
              )}
            />

            <TextField
              fullWidth
              label="Device label (optional)"
              placeholder="e.g. Shop Floor iPad #3"
              value={deviceLabel}
              onChange={(e) => setDeviceLabel(e.target.value)}
              sx={{ mb: 3 }}
              helperText="Identifies which device this credential was registered on"
            />

            <Button
              variant="contained"
              size="large"
              fullWidth
              disabled={!selectedUser || generating}
              startIcon={
                generating
                  ? <CircularProgress size={18} color="inherit" />
                  : <QrCode2Outlined />
              }
              onClick={handleGenerate}
            >
              {generating ? "Generating..." : "Generate Enrollment QR"}
            </Button>

            <Alert severity="info" sx={{ mt: 2.5 }}>
              <Typography variant="caption">
                Tokens expire in <strong>48 hours</strong> and are <strong>single-use</strong>.
                If a hire is delayed, simply generate a fresh one — the old one is invalidated automatically.
              </Typography>
            </Alert>
          </Card>

          {/* Right: QR display */}
          <Card
            sx={{
              p: 3,
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 340,
              bgcolor: result ? "background.paper" : "background.neutral",
            }}
          >
            {!result ? (
              <Stack alignItems="center" spacing={1.5}>
                <QrCode2Outlined sx={{ fontSize: 56, color: "text.disabled" }} />
                <Typography variant="body2" sx={{ color: "text.disabled" }}>
                  QR code will appear here
                </Typography>
              </Stack>
            ) : (
              <Stack alignItems="center" spacing={2}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {result.enrolleeName}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {result.enrolleeEmail}
                </Typography>

                <Box
                  sx={{
                    p: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    bgcolor: "background.paper",
                  }}
                >
                  <QRCodeSVG value={result.enrollUrl} size={200} level="M" />
                </Box>

                <Stack direction="row" spacing={1}>
                  <Chip
                    label={`Expires in ~${expiresInHours}h`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                  <Chip label="Single-use" size="small" variant="outlined" />
                </Stack>

                <Typography
                  variant="caption"
                  sx={{ color: "text.disabled", textAlign: "center", maxWidth: 240 }}
                >
                  Have the new hire scan this on the floor device.
                </Typography>

                <Button
                  size="small"
                  onClick={() => { setResult(null); setSelectedUser(null); setDeviceLabel(""); }}
                >
                  Generate another
                </Button>
              </Stack>
            )}
          </Card>
        </Stack>

        {/* ── Active tokens table */}
        <Card sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Outstanding Enrollment Tokens
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                Active tokens that have not yet been used. Revoke any that are no longer needed.
              </Typography>
            </Box>
            <Tooltip title="Refresh">
              <IconButton onClick={fetchActiveTokens} size="small">
                <RefreshOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
          <Divider sx={{ mb: 2 }} />

          {tokensLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : activeTokens.length === 0 ? (
            <Typography variant="body2" sx={{ color: "text.secondary", py: 2 }}>
              No outstanding tokens.
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <SmartTable tableId="enrollments" lockedColumns={["New Hire", "Status"]} size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "background.neutral" }}>
                    <TableCell sx={{ fontWeight: 700 }}>New Hire</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Device</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Issued By</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Generated</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activeTokens.map((t) => {
                    const { label, color } = formatExpiry(t.expiresAt, t.expired);
                    return (
                      <TableRow key={t.id} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {t.enrollee?.name ?? "—"}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "text.secondary" }}>
                            {t.enrollee?.email}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {t.deviceLabel || <span style={{ color: "#aaa" }}>—</span>}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{t.issuedBy ?? "—"}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(t.createdAt).toLocaleDateString("en-US", {
                              month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
                            })}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={label}
                            size="small"
                            color={color}
                            variant="outlined"
                            icon={t.expired ? <WarningAmberOutlined /> : undefined}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Revoke this token">
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                disabled={revoking === t.id}
                                onClick={() => handleRevoke(t.id, t.enrollee?.name ?? "user")}
                              >
                                {revoking === t.id
                                  ? <CircularProgress size={16} color="error" />
                                  : <DeleteOutline fontSize="small" />
                                }
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </SmartTable>
            </TableContainer>
          )}
        </Card>
      </Container>
    </>
  );
}
